// src/pages/Login.tsx - Refactored
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { useTranslation } from 'react-i18next';
import { useAnimatedBackground } from '../hooks/useAnimatedBackground';
import { useLoginAuth } from '../hooks/useLoginAuth';
import { LoginBackground } from '../components/LoginBackground';
import { LoginForm } from '../components/LoginForm';
import { ResetPasswordForm } from '../components/ResetPasswordForm';
import { OTPVerificationForm } from '../components/OTPVerificationForm';
import { FeaturesSlider } from '../components/FeaturesSlider';

const Login = () => {
  const [page, setPage] = useState(1);
  const cardRef = useRef<HTMLDivElement>(null);
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { canvasRef, particlesRef } = useAnimatedBackground({ cardRef });
  const {
    username,
    setUsername,
    password,
    setPassword,
    forgotemail,
    setForgotEmail,
    otp,
    setOtp,
    error,
    apiLoading,
    handleSubmit,
    handleVerify,
    handleReset,
  } = useLoginAuth();

  useEffect(() => {
    if (token != null) {
      if (role === 'TERMINAL_MANAGER') {
        navigate('/terminals');
      } else if (role === 'MSP_USER') {
        navigate('/msp-portal');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate, token, role]);

  const onLoginSubmit = async () => {
    const result = await handleSubmit();
    if (result === 'otp-required') {
      setPage(3);
    }
  };

  return (
    <div
      dir={i18n.language === 'en' ? 'ltr' : 'rtl'}
      className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] relative overflow-hidden transition-colors duration-500"
    >
      <LoginBackground canvasRef={canvasRef} particlesRef={particlesRef} />

      <div className="relative z-10 w-full max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8">
          {/* Login Forms Column */}
          <div className="w-full max-w-md lg:max-w-sm flex items-center justify-center">
            <div
              ref={cardRef}
              className={`liquid-glass ${page === 1 ? 'block' : 'hidden'} p-6 sm:p-8 rounded-2xl sm:rounded-3xl transition-all duration-500 w-full`}
            >
              <LoginForm
                username={username}
                setUsername={setUsername}
                password={password}
                setPassword={setPassword}
                error={error}
                apiLoading={apiLoading}
                onSubmit={onLoginSubmit}
                onForgotPassword={() => setPage(2)}
                t={t}
                i18n={i18n}
              />
            </div>

            <div className={`liquid-glass ${page === 2 ? 'block' : 'hidden'} p-6 sm:p-8 rounded-2xl sm:rounded-3xl transition-all duration-500 w-full`}>
              <ResetPasswordForm
                forgotemail={forgotemail}
                setForgotEmail={setForgotEmail}
                error={error}
                apiLoading={apiLoading}
                onReset={handleReset}
                onBack={() => setPage(1)}
                t={t}
              />
            </div>

            <div className={`liquid-glass ${page === 3 ? 'block' : 'hidden'} p-6 sm:p-8 rounded-2xl sm:rounded-3xl transition-all duration-500 w-full`}>
              <OTPVerificationForm
                username={username}
                otp={otp}
                setOtp={setOtp}
                error={error}
                apiLoading={apiLoading}
                onVerify={() => handleVerify(t)}
                onBack={() => setPage(1)}
                t={t}
              />
            </div>
          </div>

          {/* Features Slider Column - Visible on tablet and desktop */}
          <div className="hidden md:block w-full max-w-md lg:max-w-lg">
            <FeaturesSlider />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
